// src/components/Carousel.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import { EffectCoverflow, Pagination, Navigation } from 'swiper/modules';
import { fetchAllBooks } from '../services/AllServices';
import { getToken } from '../storage/Storage';
import '../styles/Carousel.css';

import leftArrow from '../images/new_release_carousel_leftarrow.png';
import rightArrow from '../images/new_release_carousel_rightarrow.png';
import { speakText } from './utils/speechUtils';

const Carousel = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(null);
  const navigate = useNavigate();
  const swiperRef = useRef(null);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const storedToken = await getToken();
        setTokenState(storedToken);
      } catch (err) {
        console.error('Error getting token', err);
      }
    };
    fetchToken();

    const getBooks = async () => {
      try {
        const response = await fetchAllBooks();
        const sortedBooks = response.data
          .sort((a, b) => new Date(b.created) - new Date(a.created))
          .slice(0, 6);
        setBooks(sortedBooks);
      } catch (error) {
        console.error('Error fetching books:', error);
      } finally {
        setLoading(false);
      }
    };
    getBooks();
  }, []);

  useEffect(() => {
    const updateNavigationArrows = () => {
      if (!swiperRef.current) return;
      const swiper = swiperRef.current.swiper;
      const prevButton = document.querySelector('.swiper-button-prev');
      const nextButton = document.querySelector('.swiper-button-next');

      if (prevButton) {
        if (swiper.isBeginning) prevButton.classList.add('disabled');
        else prevButton.classList.remove('disabled');
      }

      if (nextButton) {
        if (swiper.isEnd) nextButton.classList.add('disabled');
        else nextButton.classList.remove('disabled');
      }
    };

    const swiperEl = swiperRef.current;
    if (swiperEl && swiperEl.swiper) {
      swiperEl.swiper.on('slideChange', updateNavigationArrows);
      updateNavigationArrows();
    }

    return () => {
      if (swiperEl && swiperEl.swiper) {
        swiperEl.swiper.off('slideChange', updateNavigationArrows);
      }
    };
  }, [books]);

  const handleSlideClick = (bookId) => {
    if (!token) {
      navigate('/signin');
    } else {
      navigate(`/book-info/${bookId}`, { state: { books } });
    }
  };

  const handleFocusOnSlide = (bookTitle) => {
    speakText(`${bookTitle}`);
  };

  const handleFocusOnArrow = (direction) => {
    speakText(`Move ${direction} through the carousel`);
  };

  return (
    <div className="new-rleases-carousel-container">
      <Swiper
        ref={swiperRef}
        effect={'coverflow'}
        grabCursor={true}
        centeredSlides={false}
        initialSlide={0}
        loop={false}
        slidesPerView={3}
        spaceBetween={90}
        coverflowEffect={{
          rotate: 0,
          stretch: 0,
          depth: 100,
          modifier: 1,
          slideShadows: false,
        }}
        pagination={{ el: '.swiper-pagination', clickable: true }}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          clickable: true,
        }}
        modules={[EffectCoverflow, Pagination, Navigation]}
        className="swiper-container"
        breakpoints={{
          320: { slidesPerView: 3, spaceBetween: 80 },
          480: { slidesPerView: 3, spaceBetween: 30 },
          768: { slidesPerView: 3, spaceBetween: 80 },
          1024: { slidesPerView: 3, spaceBetween: 80 },
        }}
      >
        {loading ? (
          [...Array(6)].map((_, index) => (
            <SwiperSlide key={index} className="swiper-slide carousel-skeleton-item">
              <div className="carousel-skeleton-image" aria-hidden="true"></div>
            </SwiperSlide>
          ))
        ) : (
          books.map((book) => (
            <SwiperSlide
              key={book._id}
              className="swiper-slide"
              aria-roledescription="slide"
            >
              {/* Semantic button fills the slide and preserves UI */}
              <button
                type="button"
                className="carousel-slide-button"
                onClick={() => handleSlideClick(book._id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSlideClick(book._id);
                  }
                }}
                onFocus={() => handleFocusOnSlide(book.title)}
                aria-label={`${book.title} - open book details`}
              >
                <img src={book.thumbnail} alt={book.title} />
              </button>
            </SwiperSlide>
          ))
        )}

        <div className="swiper-pagination" aria-hidden="true"></div>
      </Swiper>

      <button
        className="swiper-button-prev disabled"
        onClick={() => swiperRef.current?.swiper.slidePrev()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            swiperRef.current?.swiper.slidePrev();
          }
        }}
        onFocus={() => handleFocusOnArrow('backward')}
        aria-label="Previous slide"
      >
        <img src={leftArrow} alt="Left Arrow" />
      </button>

      <button
        className="swiper-button-next"
        onClick={() => swiperRef.current?.swiper.slideNext()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            swiperRef.current?.swiper.slideNext();
          }
        }}
        onFocus={() => handleFocusOnArrow('forward')}
        aria-label="Next slide"
      >
        <img src={rightArrow} alt="Right Arrow" />
      </button>
    </div>
  );
};

export default Carousel;
